import {useContext, useEffect, useRef, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {AuthContext} from "../context/AuthContext";
import axiosInstance from "../utils/axiosInstance";
import {PlayIcon} from "@heroicons/react/24/solid";
import {GoogleLogin} from "@react-oauth/google";
import AuthLayout from "../components/AuthLayout";
import {Alert, Button, Input, buttonClass} from "../components/ui";

const Login = () => {
    const {login} = useContext(AuthContext);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();

        const loginData = {email, password};
        setIsLoading(true);

        try {
            const response = await axiosInstance.post("/login", loginData, {
                headers: {
                    "Content-Type": "application/json",
                },
            });
            const {data} = response;

            if (response.status < 200 || response.status >= 300) {
                throw new Error("Login failed!");
            }

            login(data.token, navigate);
        } catch (error) {
            setError(error.response?.data?.message || "Something went wrong");
        } finally {
            setIsLoading(false);
        }
    };

    // Google renders its own button in an iframe, so it takes props rather than
    // classes: the closest we get to the app's controls is a large pill at the
    // card's width, in the theme the page is actually rendering in.
    const googleSlotRef = useRef(null);
    const [googleWidth, setGoogleWidth] = useState(320);
    const isDarkTheme =
        typeof document !== "undefined" &&
        document.documentElement.classList.contains("dark");

    useEffect(() => {
        const slot = googleSlotRef.current;
        if (!slot || typeof ResizeObserver === "undefined") return undefined;
        // Google caps the button at 400px; below that it must match the card.
        const measure = () =>
            setGoogleWidth(Math.min(400, Math.round(slot.getBoundingClientRect().width)));
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(slot);
        return () => observer.disconnect();
    }, []);

    const closeModal = (e) => {
        // Close the modal if clicked outside the iframe container
        if (e.target.id === "modal-overlay") {
            setIsModalOpen(false);
        }
    };

    return (
        <AuthLayout
            title="Welcome back"
            subtitle="Sign in to your workspace"
            action={
                <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className={buttonClass({size: "sm"})}
                >
                    <PlayIcon className="h-4 w-4"/>
                    Watch
                </button>
            }
        >
            {error && <Alert tone="error" className="mb-4">{error}</Alert>}

            <form
                onSubmit={handleSubmit}
                className="space-y-3"
                action="/login"
                method="post"
                autoComplete="on"
            >
                <Input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    name="email"
                    id="email"
                    autoComplete="username"
                    required
                />
                <Input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    name="password"
                    id="password"
                    autoComplete="current-password"
                    required
                />
                <div className="text-right">
                    <Link
                        to="/forgot-password"
                        className="text-xs font-semibold text-red-600 transition hover:text-red-500 dark:text-orange-400"
                    >
                        Forgot Password?
                    </Link>
                </div>
                <button type="submit" disabled={isLoading} className={buttonClass({className: "w-full"})}>
                    {isLoading ? "Logging in…" : "LOG IN"}
                </button>
            </form>

            <Button to="/register" variant="subtle" className="mt-3 w-full">
                CREATE NEW ACCOUNT
            </Button>

            <div className="mt-6">
                <div className="mb-4 flex items-center gap-3">
                    <span className="h-px flex-1 bg-gray-200 dark:bg-[#2c2c2e]"/>
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-slate-500">
                        or
                    </span>
                    <span className="h-px flex-1 bg-gray-200 dark:bg-[#2c2c2e]"/>
                </div>
                <div ref={googleSlotRef} className="flex justify-center">
                    <GoogleLogin
                        width={googleWidth}
                        shape="pill"
                        size="large"
                        text="signin_with"
                        locale="en"
                        theme={isDarkTheme ? "filled_black" : "outline"}
                        onSuccess={async (credentialResponse) => {
                            const idToken = credentialResponse.credential;
                            setIsLoading(true);
                            try {
                                const response = await axiosInstance.post("/auth/google", {
                                    idToken,
                                });
                                const {token} = response.data;
                                login(token, navigate);
                            } catch (error) {
                                setError("Google login failed");
                            } finally {
                                setIsLoading(false);
                            }
                        }}
                        onError={() => {
                            setError("Google login was unsuccessful");
                        }}
                    />
                </div>
            </div>

            {isModalOpen && (
                <div
                    id="modal-overlay"
                    className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
                    onClick={closeModal}
                >
                    <div className="w-full max-w-3xl overflow-hidden rounded-[18px] border border-gray-200 bg-black shadow-2xl dark:border-[#2c2c2e]">
                        <div className="relative h-0 pb-[56.25%]">
                            <iframe
                                className="absolute left-0 top-0 h-full w-full"
                                src="https://www.youtube.com/embed/hwacIuP2NDA?si=hpGCVmiGdGO95YoI"
                                title="YouTube video player"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                referrerPolicy="strict-origin-when-cross-origin"
                                allowFullScreen
                            ></iframe>
                        </div>
                    </div>
                </div>
            )}
        </AuthLayout>
    );
};

export default Login;
